import React, { useState, useRef } from 'react';
import { 
  X, 
  Upload, 
  Link as LinkIcon, 
  FileText, 
  Video, 
  Book,
  AlertCircle,
  CheckCircle,
  Loader,
 GraduationCap,
  Globe
} from 'lucide-react';
import { useAuth } from '../../../core/contexts/AuthContext';

const ResourceUploadModal = ({ 
  isOpen, 
  onClose, 
  onUpload, 
  uploading = false,
  subjects = [] 
}) => {
  const { userProfile } = useAuth();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    subject: '',
    resourceType: 'video',
    link: '',
    customSubject: '',
    applicableToAllUniversities: false // New field
  });
  const [file, setFile] = useState(null);
  const [uploadMethod, setUploadMethod] = useState('link'); // 'link' or 'file'
  const [errors, setErrors] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Get user's university from profile
  const userUniversity = userProfile?.academic?.university || 'Unknown University';

  const resourceTypes = [
    { value: 'video', label: 'Video', icon: Video, description: 'YouTube videos, video tutorials' },
    { value: 'pdf', label: 'PDF', icon: FileText, description: 'PDF documents, guides' },
    { value: 'document', label: 'Document', icon: FileText, description: 'Word docs, text files' },
    { value: 'past papers', label: 'Past Papers', icon: Book, description: 'Exam papers, practice tests' },
    { value: 'link', label: 'External Link', icon: LinkIcon, description: 'Websites, online resources' }
  ];

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setFormData({
        title: '',
        description: '',
        subject: '',
        resourceType: 'video',
        link: '',
        customSubject: '',
        applicableToAllUniversities: false
      });
      setFile(null);
      setUploadMethod('link');
      setErrors({});
      setDragActive(false);
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({ ...prev, [name]: fieldValue }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileSelect = (selectedFile) => {
    if (selectedFile) {
      const maxSize = 50 * 1024 * 1024; // 50MB
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
        'video/mp4',
        'video/avi',
        'video/mov'
      ];

      if (selectedFile.size > maxSize) {
        setErrors(prev => ({ ...prev, file: 'File size must be less than 50MB' }));
        return;
      }

      if (!allowedTypes.includes(selectedFile.type)) {
        setErrors(prev => ({ 
          ...prev, 
          file: 'Unsupported file type. Please upload PDF, Word, text, or video files.' 
        }));
        return;
      }

      setFile(selectedFile);
      setErrors(prev => ({ ...prev, file: '' }));

      // Auto-detect resource type based on file
      if (selectedFile.type === 'application/pdf') {
        setFormData(prev => ({ ...prev, resourceType: 'pdf' }));
      } else if (selectedFile.type.startsWith('video/')) {
        setFormData(prev => ({ ...prev, resourceType: 'video' }));
      } else if (selectedFile.type.includes('word') || selectedFile.type === 'text/plain') {
        setFormData(prev => ({ ...prev, resourceType: 'document' }));
      }
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.subject && !formData.customSubject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (uploadMethod === 'link') {
      if (!formData.link.trim()) {
        newErrors.link = 'Link is required';
      } else if (!isValidUrl(formData.link)) {
        newErrors.link = 'Please enter a valid URL';
      }
    } else {
      if (!file) {
        newErrors.file = 'Please select a file to upload';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const finalSubject = formData.subject || formData.customSubject.trim();
    
    const resourceData = {
      title: formData.title.trim(),
      description: formData.description.trim(),
      subject: finalSubject,
      resourceType: formData.resourceType,
      link: uploadMethod === 'link' ? formData.link.trim() : '',
      // Add university information
      university: formData.applicableToAllUniversities ? 'All Universities' : userUniversity,
      submittedByUniversity: userUniversity, // Always track the submitter's university
      applicableToAllUniversities: formData.applicableToAllUniversities
    };

    const success = await onUpload(resourceData, uploadMethod === 'file' ? file : null);
    
    if (success) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-6 pt-6 pb-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">
                Upload Resource
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 transition-colors"
                disabled={uploading}
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="mt-2 text-sm text-gray-600">
              Share educational resources with your fellow students
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 pb-6">
            {/* Upload Method Toggle */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Upload Method
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setUploadMethod('link')}
                  className={`p-3 border rounded-lg text-sm font-medium transition-colors ${
                    uploadMethod === 'link'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <LinkIcon className="w-5 h-5 mx-auto mb-1" />
                  External Link
                </button>
                <button
                  type="button"
                  onClick={() => setUploadMethod('file')}
                  className={`p-3 border rounded-lg text-sm font-medium transition-colors ${
                    uploadMethod === 'file'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Upload className="w-5 h-5 mx-auto mb-1" />
                  Upload File
                </button>
              </div>
            </div>

            {/* Title */}
            <div className="mb-4">
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  errors.title ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter resource title"
                disabled={uploading}
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.title}
                </p>
              )}
            </div>

            {/* Description */}
            <div className="mb-4">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  errors.description ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Describe what this resource covers"
                disabled={uploading}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.description}
                </p>
              )}
            </div>

            {/* Subject */}
            <div className="mb-4">
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                Subject *
              </label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  errors.subject ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={uploading}
              >
                <option value="">Select a subject</option>
                {subjects.map((subject) => (
                  <option key={subject} value={subject}>
                    {subject}
                  </option>
                ))}
                <option value="">Other (specify below)</option>
              </select>
              
              {!formData.subject && (
                <input
                  type="text"
                  name="customSubject"
                  value={formData.customSubject}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Enter custom subject"
                  disabled={uploading}
                />
              )}
              
              {errors.subject && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.subject}
                </p>
              )}
            </div>

            {/* University Applicability */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                University Applicability
              </label>
              
              {/* Current University Display */}
              <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center text-sm text-gray-600">
                  <GraduationCap className="w-4 h-4 mr-2" />
                  <span>Your University: <strong>{userUniversity}</strong></span>
                </div>
              </div>

              {/* Toggle for All Universities */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="applicableToAllUniversities"
                  name="applicableToAllUniversities"
                  checked={formData.applicableToAllUniversities}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  disabled={uploading}
                />
                <label htmlFor="applicableToAllUniversities" className="ml-2 block text-sm text-gray-700">
                  <div className="flex items-center">
                    <Globe className="w-4 h-4 mr-1 text-green-500" />
                    <span>Make this resource available to students from all universities</span>
                  </div>
                </label>
              </div>
              
              <p className="mt-2 text-xs text-gray-500">
                {formData.applicableToAllUniversities 
                  ? "This resource will be visible to students from all universities"
                  : `This resource will only be visible to students from ${userUniversity}`
                }
              </p>
            </div>

            {/* Resource Type */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Resource Type *
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {resourceTypes.map((type) => {
                  const IconComponent = type.icon;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, resourceType: type.value }))}
                      className={`p-3 border rounded-lg text-left transition-colors ${
                        formData.resourceType === type.value
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                      disabled={uploading}
                    >
                      <IconComponent className={`w-5 h-5 mb-2 ${
                        formData.resourceType === type.value ? 'text-green-600' : 'text-gray-400'
                      }`} />
                      <div className={`font-medium text-sm ${
                        formData.resourceType === type.value ? 'text-green-900' : 'text-gray-900'
                      }`}>
                        {type.label}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {type.description}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Upload Method Content */}
            {uploadMethod === 'link' ? (
              <div className="mb-6">
                <label htmlFor="link" className="block text-sm font-medium text-gray-700 mb-2">
                  Resource URL *
                </label>
                <input
                  type="url"
                  id="link"
                  name="link"
                  value={formData.link}
                  onChange={handleInputChange}
                  className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.link ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="https://example.com/resource"
                  disabled={uploading}
                />
                {errors.link && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.link}
                  </p>
                )}
              </div>
            ) : (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload File *
                </label>
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    dragActive
                      ? 'border-green-400 bg-green-50'
                      : errors.file
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={(e) => handleFileSelect(e.target.files[0])}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.txt,.mp4,.avi,.mov"
                    disabled={uploading}
                  />
                  
                  {file ? (
                    <div className="text-green-600">
                      <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                      <p className="font-medium">{file.name}</p>
                      <p className="text-sm text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                      <button
                        type="button"
                        onClick={() => setFile(null)}
                        className="mt-2 text-sm text-red-600 hover:text-red-700"
                        disabled={uploading}
                      >
                        Remove file
                      </button>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-gray-600 mb-2">
                        Drop your file here, or{' '}
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="text-green-600 hover:text-green-700 font-medium"
                          disabled={uploading}
                        >
                          browse
                        </button>
                      </p>
                      <p className="text-xs text-gray-500">
                        Supports PDF, Word, Text, and Video files (max 50MB)
                      </p>
                    </div>
                  )}
                </div>
                {errors.file && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.file}
                  </p>
                )}
              </div>
            )}

            {/* Info Box */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-700">
                  <p className="font-medium mb-1">Review Process</p>
                  <p>
                    Your resource will be reviewed by our team before being published. 
                    You'll be notified once it's approved and available to other students.
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={uploading}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {uploading ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Resource
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResourceUploadModal;